// src/services/AuthService.ts
import { jwtDecode } from 'jwt-decode';
import api from './api';
import TokenService from './TokenService';
import { LoginDto, LoginResponse, UserResponse } from '../shared/dtos/Auth.dto';

interface JwtPayload {
  sub: number;
  username: string;
  permission: string;
  iat: number;
  exp: number;
}

class AuthService {
  async login(data: LoginDto): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>("/user/login", data);
      const { accessToken, user } = response.data;

      // Validação dos dados do usuário
      if (!user || !user.name || !user.email || !user.permission) {
        throw new Error('Dados do usuário incompletos na resposta do servidor');
      }

      // Verifica se o usuário está ativo
      if (user.isActive === false) {
        throw new Error('Usuário inativo. Entre em contato com o administrador do sistema.');
      }

      // Valida o token recebido
      if (!TokenService.validateToken(accessToken)) {
        throw new Error('Token inválido recebido do servidor');
      }

      // Decodifica o token para obter o ID do usuário
      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      const userId = decodedToken.sub;

      if (!userId || userId <= 0) {
        throw new Error('ID de usuário inválido no token');
      }

      // Salva o token usando o TokenService
      TokenService.setToken(accessToken);

      // Prepara o objeto de usuário para armazenamento
      const userToStore: UserResponse = {
        id: userId,
        name: user.name,
        email: user.email,
        permission: user.permission,
        isActive: user.isActive ?? true,
        controleAcesso: user.controleAcesso,
      };


      // Salva os dados do usuário
      sessionStorage.setItem("user", JSON.stringify(userToStore));

      return {
        accessToken,
        user: userToStore
      };
    } catch (error: any) {
      // Se for erro de conexão ou resposta do servidor
      if (error.response) {
        const message = error.response.data?.message || 'Erro ao fazer login';
        throw new Error(message);
      }
      // Se for erro de rede ou timeout
      if (error.request) {
        throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão.');
      }
      // Se for erro de validação local
      throw error;
    }
  }

  async loginByToken(oldToken: string): Promise<LoginResponse> {
    const data = {
      oldToken: oldToken
    }
    try {
      const response = await api.post<LoginResponse>("/user/login-by-token", data);
      const { accessToken, user } = response.data;

      // Validação dos dados do usuário
      if (!user || !user.name || !user.email || !user.permission) {
        throw new Error('Dados do usuário incompletos na resposta do servidor');
      }

      // Verifica se o usuário está ativo
      if (user.isActive === false) {
        throw new Error('Usuário inativo. Entre em contato com o administrador do sistema.');
      }

      // Valida o token recebido
      if (!TokenService.validateToken(accessToken)) {
        throw new Error('Token inválido recebido do servidor');
      }

      // Decodifica o token para obter o ID do usuário
      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      const userId = decodedToken.sub;

      if (!userId || userId <= 0) {
        throw new Error('ID de usuário inválido no token');
      }

      // Salva o token usando o TokenService
      TokenService.setToken(accessToken);

      // Prepara o objeto de usuário para armazenamento
      const userToStore = {
        id: userId,
        name: user.name,
        email: user.email,
        permission: user.permission,
        isActive: user.isActive ?? true,
      };

      const userToAuth: UserResponse = {
        id: userId,
        name: user.name,
        email: user.email,
        permission: user.permission,
        isActive: user.isActive ?? true,
        controleAcesso: user.controleAcesso,
      };


      // Salva os dados do usuário
      sessionStorage.setItem("user", JSON.stringify(userToStore));

      return {
        accessToken,
        user: userToAuth
      };
    } catch (error: any) {
      // Se for erro de conexão ou resposta do servidor
      if (error.response) {
        const message = error.response.data?.message || 'Erro ao fazer login';
        throw new Error(message);
      }

      // Se for erro de rede ou timeout
      if (error.request) {
        throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão.');
      }

      // Se for erro de validação local
      throw error;
    }
  }

  logout(): void {
    TokenService.clearToken();
    sessionStorage.removeItem("user");
  }

  isAuthenticated(): boolean {
    const token = TokenService.getToken();
    return token !== null && !TokenService.isTokenExpired();
  }
}

const authService = new AuthService();
export default authService;
